import { ReactNode, createContext, useCallback, useContext } from 'react'
import { useSearchParams } from 'react-router'

import { useShallowMemo } from 'src/hooks/useShallowMemo'
import { isMockActive, REQUESTED_LANGUAGE_PARAM } from 'src/utils/env'
import { Language, Translation } from 'src/models/Language'
import { getLanguageByCode } from 'src/utils/language'
import { isLanguageSelectionAvailable } from 'src/models/TranscriptMetadata'

import { useTranscriptMetadata } from './Transcript/MetadataContext'

type LanguageContextValue = {
    /** The currently selected language code (always populated, never empty) */
    requestedLanguageCode: string
    /** Empty string when viewing original language, populated for translations (for backend APIs) */
    translatedLanguageCode: string
    /** The currently selected language name */
    requestedLanguageName: Language
    /** The original language code of the transcript */
    originalLanguageCode: string
    /** True when viewing the original language (no translation selected) */
    isOriginalLanguage: boolean
    /** Available translations for this transcript */
    translations: Translation[]
    /** Function to switch to a different language */
    requestLanguage: (languageCode: string) => void
    /** Whether language selection is available for this transcript */
    isLanguageSelectionAvailable: boolean
}

const LanguageContext = createContext<LanguageContextValue | null>(null)

type LanguageProviderProps = {
    children: ReactNode
}

export const LanguageProvider = (props: LanguageProviderProps) => {
    const { children } = props
    const metadata = useTranscriptMetadata()
    const [searchParams, setSearchParams] = useSearchParams()

    const originalLanguageCode = metadata?.originalLanguageCode || ''
    const queryParamLanguageCode = searchParams.get(REQUESTED_LANGUAGE_PARAM) || ''
    const translatedLanguageCode = queryParamLanguageCode === originalLanguageCode ? '' : queryParamLanguageCode
    const requestedLanguageCode = translatedLanguageCode || originalLanguageCode
    const isOriginalLanguage = !translatedLanguageCode
    const requestedLanguageName = getLanguageByCode(requestedLanguageCode)

    const requestLanguage = useCallback(
        (languageCode: string) => {
            const newLanguageCode = languageCode === originalLanguageCode ? undefined : languageCode

            if (isMockActive()) {
                // For mock mode, still use full page reload
                const newUrl = new URL(window.location.href)
                if (newLanguageCode) {
                    newUrl.searchParams.set(REQUESTED_LANGUAGE_PARAM, newLanguageCode)
                } else {
                    newUrl.searchParams.delete(REQUESTED_LANGUAGE_PARAM)
                }
                window.location.assign(newUrl)
            } else {
                // Use React Router's setSearchParams
                setSearchParams(
                    (prev) => {
                        const newParams = new URLSearchParams(prev)
                        if (newLanguageCode) {
                            newParams.set(REQUESTED_LANGUAGE_PARAM, newLanguageCode)
                        } else {
                            newParams.delete(REQUESTED_LANGUAGE_PARAM)
                        }

                        return newParams
                    },
                    { replace: false },
                )
            }
        },
        [originalLanguageCode, setSearchParams],
    )

    return (
        <LanguageContext.Provider
            value={useShallowMemo({
                requestedLanguageCode,
                translatedLanguageCode,
                requestedLanguageName,
                originalLanguageCode,
                isOriginalLanguage,
                translations: metadata?.translations || [],
                requestLanguage,
                isLanguageSelectionAvailable: isLanguageSelectionAvailable(metadata),
            })}>
            {children}
        </LanguageContext.Provider>
    )
}

export const useLanguage = () => {
    const contextValue = useContext(LanguageContext)

    if (contextValue === null) {
        throw new Error('You have forgot to use <LanguageContext.Provider />, shame on you.')
    }

    return contextValue
}
